import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export const Layout: React.FC = () => {
  const { user, isAuthenticated, isDemoEditor, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = React.useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const startsWith = (path: string) => location.pathname.startsWith(path);
  const storedWorkspace =
    typeof window !== 'undefined' ? window.localStorage.getItem('activeWorkspace') : null;
  const activeWorkspace: 'demo' | 'logo' =
    startsWith('/logos') || startsWith('/admin/logos')
      ? 'logo'
      : startsWith('/demos') || (startsWith('/admin') && !startsWith('/admin/logos'))
        ? 'demo'
        : storedWorkspace === 'logo'
          ? 'logo'
          : 'demo';

  React.useEffect(() => {
    window.localStorage.setItem('activeWorkspace', activeWorkspace);
  }, [activeWorkspace]);

  const submitPath = activeWorkspace === 'logo' ? '/logos/submit' : '/demos/submit';
  const submitLabel = activeWorkspace === 'logo' ? 'Submit Logo' : 'Submit Demo';
  const adminPath = activeWorkspace === 'logo' ? '/admin/logos' : '/admin';
  const appTitle = activeWorkspace === 'logo' ? 'Customer Logo Search' : 'Customer Demo Search';
  const showWorkspaceActions = location.pathname !== '/';

  const isSubmitActive =
    activeWorkspace === 'logo' ? startsWith('/logos/submit') : startsWith('/demos/submit');
  const isLogoSearchActive = startsWith('/logos/search');
  const isAdminActive =
    activeWorkspace === 'logo' ? startsWith('/admin/logos') : startsWith('/admin') && !startsWith('/admin/logos');

  React.useEffect(() => {
    setShowUserMenu(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-white">
      {/* Top Navigation */}
      <nav className="relative z-[90] border-b border-slate-200/80 bg-white/90 shadow-sm backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Left side - Logo and Navigation */}
            <div className="flex items-center">
              {/* Logo */}
              <Link to="/" className="flex items-center">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/6/66/Coveo-logo-2021.png"
                  alt="Coveo"
                  className="h-8 w-auto object-contain"
                  referrerPolicy="no-referrer"
                />
                <div className="ml-2">
                  <span className="text-xl font-bold text-gray-900">{appTitle}</span>
                </div>
              </Link>

              {/* Navigation Tabs */}
              {isAuthenticated && (
                <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
                  <Link
                    to="/demos/search"
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      startsWith('/demos/search')
                        ? 'bg-coveo-blue text-white'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1.5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 11H5m14 0a2 2 0 100-4H5a2 2 0 000 4m14 0v6a2 2 0 01-2 2H7a2 2 0 01-2-2v-6m14 0H5"
                        />
                      </svg>
                      Demo Search
                    </span>
                  </Link>
                  {!isLogoSearchActive && (
                    <Link
                      to="/logos/search"
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        startsWith('/logos/search')
                          ? 'bg-coveo-orange text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center">
                        <svg
                          className="w-4 h-4 mr-1.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                          />
                        </svg>
                        Logo Search
                      </span>
                    </Link>
                  )}
                  {showWorkspaceActions && !(activeWorkspace === 'logo' && isSubmitActive) && (
                    <Link
                      to={submitPath}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isSubmitActive
                          ? activeWorkspace === 'logo'
                            ? 'bg-coveo-orange text-white'
                            : 'bg-coveo-blue text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center">
                        <svg
                          className="w-4 h-4 mr-1.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 4v16m8-8H4"
                          />
                        </svg>
                        {submitLabel}
                      </span>
                    </Link>
                  )}
                  
                  {/* Admin link - only visible to demo editors */}
                  {isDemoEditor && showWorkspaceActions && (
                    <Link
                      to={adminPath}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        isAdminActive
                          ? activeWorkspace === 'logo'
                            ? 'bg-coveo-orange text-white'
                            : 'bg-coveo-blue text-white'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center">
                        <svg
                          className="w-4 h-4 mr-1.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                        Admin
                      </span>
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* Right side - User Menu */}
            <div className="flex items-center">
              {isAuthenticated && user ? (
                <div className="relative z-[110]">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none"
                  >
                    <div className="w-8 h-8 bg-coveo-orange rounded-full flex items-center justify-center text-white font-medium">
                      {user.email.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden md:block text-sm font-medium">
                      {user.email}
                    </span>
                    <svg
                      className={`w-4 h-4 transition-transform ${
                        showUserMenu ? 'rotate-180' : ''
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 mt-2 w-56 rounded-md bg-white py-1 shadow-2xl ring-1 ring-black ring-opacity-5 z-[120]">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm text-gray-500">Signed in as</p>
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.email}
                        </p>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-4">
                  <Link
                    to="/login"
                    className="text-gray-600 hover:text-gray-900 text-sm font-medium"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/signup"
                    className="bg-coveo-orange text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-orange-600 transition-colors"
                  >
                    Sign up
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isAuthenticated && (
          <div className="sm:hidden border-t border-gray-200">
            <div className="flex">
              <Link
                to="/demos/search"
                className={`flex-1 py-3 text-center text-sm font-medium ${
                  startsWith('/demos/search')
                    ? 'text-coveo-blue border-b-2 border-coveo-blue'
                    : 'text-gray-500'
                }`}
              >
                Demos
              </Link>
              {!isLogoSearchActive && (
                <Link
                  to="/logos/search"
                  className={`flex-1 py-3 text-center text-sm font-medium ${
                    startsWith('/logos/search')
                      ? 'text-coveo-orange border-b-2 border-coveo-orange'
                      : 'text-gray-500'
                  }`}
                >
                  Logos
                </Link>
              )}
              {showWorkspaceActions && !(activeWorkspace === 'logo' && isSubmitActive) && (
                <Link
                  to={submitPath}
                  className={`flex-1 py-3 text-center text-sm font-medium ${
                    isSubmitActive
                      ? activeWorkspace === 'logo'
                        ? 'text-coveo-orange border-b-2 border-coveo-orange'
                        : 'text-coveo-blue border-b-2 border-coveo-blue'
                      : 'text-gray-500'
                  }`}
                >
                  {activeWorkspace === 'logo' ? 'Submit Logo' : 'Submit Demo'}
                </Link>
              )}
              {isDemoEditor && showWorkspaceActions && (
                <Link
                  to={adminPath}
                  className={`flex-1 py-3 text-center text-sm font-medium ${
                    isAdminActive
                      ? activeWorkspace === 'logo'
                        ? 'text-coveo-orange border-b-2 border-coveo-orange'
                        : 'text-coveo-blue border-b-2 border-coveo-blue'
                      : 'text-gray-500'
                  }`}
                >
                  Admin
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Click outside to close menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-[100]"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Main Content */}
      <main className="relative z-0">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
